import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Switch, Input } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { ClientRules, CommonRules } from "../styles/rules/index";
import { acceptSpan, markSpanRejected } from "../styles/Customstyles";

export const ClientCopyeditPanel = ({ editor, onClose }) => {
  const [activeTab, setActiveTab] = useState("suggestions");
  const [ruleScope, setRuleScope] = useState("all");
  const [enabledRules, setEnabledRules] = useState({});
  const [ruleSearch, setRuleSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [collapsedRules, setCollapsedRules] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [suggestionPositions, setSuggestionPositions] = useState({});
  const [scanning, setScanning] = useState(false);
  const [filterStatus, setFilterStatus] = useState("to_review"); // 'to_review', 'accepted', 'dismissed'
  const [acceptedSuggestions, setAcceptedSuggestions] = useState([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState([]);

  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Dynamic available clients derived from ClientRules
  const availableClients = useMemo(() => {
    return Object.entries(ClientRules || {}).map(([key, clientObj]) => ({
      key,
      name: clientObj.name || key.toUpperCase(),
      label: `Client (${key.toUpperCase()})`
    }));
  }, []);

  // Combine rules list based on the active scope filter
  const allAvailableRules = useMemo(() => {
    const rules = [];
    if (ruleScope === "all" || ruleScope === "common") {
      rules.push(...CommonRules.map(r => ({
        ...r,
        origin: "Common",
        clientKey: "common",
        category: r.category || "General"
      })));
    }
    if (ruleScope === "all") {
      Object.entries(ClientRules || {}).forEach(([clientKey, clientObj]) => {
        if (clientObj?.rules) {
          rules.push(...clientObj.rules.map(r => ({
            ...r,
            origin: clientObj.name || clientKey.toUpperCase(),
            clientKey: clientKey,
            category: r.category || "General"
          })));
        }
      });
    } else if (ruleScope !== "common") {
      const clientObj = ClientRules[ruleScope];
      if (clientObj?.rules) {
        rules.push(...clientObj.rules.map(r => ({
          ...r,
          origin: clientObj.name || ruleScope.toUpperCase(),
          clientKey: ruleScope,
          category: r.category || "General"
        })));
      }
    }
    return rules;
  }, [ruleScope]);

  // Initialize enabled rules map when available rules change
  useEffect(() => {
    setEnabledRules(prev => {
      const next = { ...prev };
      allAvailableRules.forEach(rule => {
        if (next[rule.id] === undefined) {
          next[rule.id] = true;
        }
      });
      return next;
    });
  }, [allAvailableRules]);

  // Run the copyediting scan
  const runScan = useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;

    setScanning(true);
    try {
      const doc = ed.getDoc();
      if (doc) {
        doc.querySelectorAll(".client-style-highlight").forEach(span => {
          const p = span.parentNode;
          if (p) {
            while (span.firstChild) p.insertBefore(span.firstChild, span);
            p.removeChild(span);
          }
        });
      }

      const activeRules = allAvailableRules.filter(r => enabledRules[r.id] !== false);
      const newSuggestions = [];

      activeRules.forEach(rule => {
        const matches = rule.execute(ed);
        if (matches && matches.length > 0) {
          newSuggestions.push({
            ruleId: rule.id,
            ruleName: rule.name,
            description: rule.description,
            doc: rule.doc,
            origin: rule.origin,
            clientKey: rule.clientKey,
            category: rule.category || "General",
            items: matches.map(m => ({ ...m, status: "pending" }))
          });
        }
      });
      setSuggestions(newSuggestions);

      // Calculate character position for each suggestion span
      const posMap = {};
      if (doc && doc.body) {
        const spans = doc.querySelectorAll("span[data-suggestion-uid]");
        spans.forEach(span => {
          const uid = span.getAttribute("data-suggestion-uid");
          if (!uid) return;
          try {
            const range = doc.createRange();
            range.selectNodeContents(doc.body);
            range.setEndBefore(span);
            posMap[uid] = range.toString().length;
          } catch (err) {}
        });
      }
      setSuggestionPositions(posMap);

      setCollapsedRules({});
      setAcceptedSuggestions([]);
      setDismissedSuggestions([]);
      setFilterStatus("to_review");
      setActiveTab("suggestions");
    } catch (err) {
      console.error("Copyediting scan failed:", err);
    } finally {
      setScanning(false);
    }
  }, [allAvailableRules, enabledRules]);

  // Single item approve
  const handleApprove = (ruleId, itemIndex) => {
    const ruleGroup = suggestions.find(s => s.ruleId === ruleId);
    if (!ruleGroup) return;
    const item = ruleGroup.items[itemIndex];
    if (!item) return;

    const ed = editorRef.current;
    if (!ed) return;

    ed.undoManager.transact(() => {
      acceptSpan(ed, item.uid, item.suggested);
    });

    setAcceptedSuggestions(prev => [...prev, { ...item, ruleName: ruleGroup.ruleName }]);
    setSuggestions(prev =>
      prev.map(sg => {
        if (sg.ruleId !== ruleId) return sg;
        return {
          ...sg,
          items: sg.items.filter((_, idx) => idx !== itemIndex)
        };
      }).filter(sg => sg.items.length > 0)
    );
  };

  // Single item reject
  const handleReject = (ruleId, itemIndex) => {
    const ruleGroup = suggestions.find(s => s.ruleId === ruleId);
    if (!ruleGroup) return;
    const item = ruleGroup.items[itemIndex];
    if (!item) return;

    const ed = editorRef.current;
    if (!ed) return;

    markSpanRejected(ed, item.uid);

    setDismissedSuggestions(prev => [...prev, { ...item, ruleName: ruleGroup.ruleName }]);
    setSuggestions(prev =>
      prev.map(sg => {
        if (sg.ruleId !== ruleId) return sg;
        return {
          ...sg,
          items: sg.items.filter((_, idx) => idx !== itemIndex)
        };
      }).filter(sg => sg.items.length > 0)
    );
  };

  // Apply all items under a single style rule
  const handleApproveStyle = (ruleId) => {
    const ruleGroup = suggestions.find(s => s.ruleId === ruleId);
    if (!ruleGroup) return;
    const ed = editorRef.current;
    if (!ed) return;

    ed.undoManager.transact(() => {
      ruleGroup.items.forEach(item => {
        acceptSpan(ed, item.uid, item.suggested);
      });
    });

    setAcceptedSuggestions(prev => [
      ...prev,
      ...ruleGroup.items.map(item => ({ ...item, ruleName: ruleGroup.ruleName }))
    ]);
    setSuggestions(prev => prev.filter(s => s.ruleId !== ruleId));
  };

  // Reject all items under a single style rule
  const handleRejectStyle = (ruleId) => {
    const ruleGroup = suggestions.find(s => s.ruleId === ruleId);
    if (!ruleGroup) return;
    const ed = editorRef.current;
    if (!ed) return;

    ruleGroup.items.forEach(item => {
      markSpanRejected(ed, item.uid);
    });

    setDismissedSuggestions(prev => [
      ...prev,
      ...ruleGroup.items.map(item => ({ ...item, ruleName: ruleGroup.ruleName }))
    ]);
    setSuggestions(prev => prev.filter(s => s.ruleId !== ruleId));
  };

  // Global approve all across all styles
  const handleApproveAll = () => {
    const ed = editorRef.current;
    if (!ed) return;

    ed.undoManager.transact(() => {
      suggestions.forEach(group => {
        group.items.forEach(item => {
          acceptSpan(ed, item.uid, item.suggested);
        });
      });
    });

    setAcceptedSuggestions(prev => [
      ...prev,
      ...suggestions.flatMap(g => g.items.map(item => ({ ...item, ruleName: g.ruleName })))
    ]);
    setSuggestions([]);
  };

  // Global reject all across all styles
  const handleRejectAll = () => {
    const ed = editorRef.current;
    if (!ed) return;

    suggestions.forEach(group => {
      group.items.forEach(item => {
        markSpanRejected(ed, item.uid);
      });
    });

    setDismissedSuggestions(prev => [
      ...prev,
      ...suggestions.flatMap(g => g.items.map(item => ({ ...item, ruleName: g.ruleName })))
    ]);
    setSuggestions([]);
  };

  // Scroll to suggestion in TinyMCE iframe
  const scrollToItem = (item) => {
    const ed = editorRef.current;
    const doc = ed?.getDoc();
    if (!doc || !ed) return;

    const span = doc.querySelector(`span[data-suggestion-uid="${item.uid}"]`);
    if (!span) return;

    ed.selection.select(span);
    span.scrollIntoView({ behavior: "smooth", block: "center" });
    ed.focus();
    ed.fire("SelectionChange");
  };

  // Collapse / Expand toggle handlers
  const toggleCollapseRule = (ruleId) => {
    setCollapsedRules(prev => ({
      ...prev,
      [ruleId]: !prev[ruleId]
    }));
  };

  const areAllCollapsed = useMemo(() => {
    if (suggestions.length === 0) return false;
    return suggestions.every(g => !!collapsedRules[g.ruleId]);
  }, [suggestions, collapsedRules]);

  const toggleAllCollapse = () => {
    if (areAllCollapsed) {
      setCollapsedRules({});
    } else {
      const all = {};
      suggestions.forEach(g => {
        all[g.ruleId] = true;
      });
      setCollapsedRules(all);
    }
  };

  // Filtered rules for Config tab
  const filteredRulesList = useMemo(() => {
    return allAvailableRules.filter(rule => {
      const term = ruleSearch.toLowerCase();
      return (
        rule.name.toLowerCase().includes(term) ||
        (rule.description && rule.description.toLowerCase().includes(term)) ||
        (rule.category && rule.category.toLowerCase().includes(term))
      );
    });
  }, [allAvailableRules, ruleSearch]);

  // Group rules by Category for Config tab
  const rulesByCategory = useMemo(() => {
    const grouped = {};
    filteredRulesList.forEach(rule => {
      const cat = rule.category || "General";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(rule);
    });
    return grouped;
  }, [filteredRulesList]);

  // Toggle individual rule
  const toggleRule = (ruleId, checked) => {
    setEnabledRules(prev => ({
      ...prev,
      [ruleId]: checked
    }));
  };

  // Toggle all rules in a category
  const toggleCategory = (categoryName, enable) => {
    const catRules = rulesByCategory[categoryName] || [];
    setEnabledRules(prev => {
      const next = { ...prev };
      catRules.forEach(r => {
        next[r.id] = enable;
      });
      return next;
    });
  };

  // Collapse / Expand toggle handlers for categories in Rules tab
  const categoryNames = useMemo(() => Object.keys(rulesByCategory), [rulesByCategory]);
  const areAllCategoriesCollapsed = useMemo(() => {
    if (categoryNames.length === 0) return false;
    return categoryNames.every(cat => !!collapsedCategories[cat]);
  }, [categoryNames, collapsedCategories]);

  const toggleCollapseCategory = (categoryName) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleAllCategoriesCollapse = () => {
    if (areAllCategoriesCollapsed) {
      setCollapsedCategories({});
    } else {
      const all = {};
      categoryNames.forEach(cat => {
        all[cat] = true;
      });
      setCollapsedCategories(all);
    }
  };

  const toReviewCount = suggestions.reduce((sum, sg) => sum + sg.items.length, 0);
  const acceptedCount = acceptedSuggestions.length;
  const dismissedCount = dismissedSuggestions.length;
  const totalEdits = toReviewCount + acceptedCount + dismissedCount;
  const progressPercent = totalEdits > 0 ? Math.round(((acceptedCount + dismissedCount) / totalEdits) * 100) : 0;

  // Reusable dynamic scope selector
  const renderScopeSelector = () => (
    <div className="flex flex-wrap gap-1.5 text-xs font-semibold">
      <button
        onClick={() => setRuleScope("all")}
        className={`px-3 py-1 rounded-full border transition-all cursor-pointer ${
          ruleScope === "all"
            ? "bg-[#EEF0FF] border-[#4F46E5] text-[#4F46E5]"
            : "bg-[#FFFFFF] border-[#E7E4DD] text-[#6B6B76] hover:text-[#17171A]"
        }`}
      >
        All
      </button>
      <button
        onClick={() => setRuleScope("common")}
        className={`px-3 py-1 rounded-full border transition-all cursor-pointer ${
          ruleScope === "common"
            ? "bg-[#EEF0FF] border-[#4F46E5] text-[#4F46E5]"
            : "bg-[#FFFFFF] border-[#E7E4DD] text-[#6B6B76] hover:text-[#17171A]"
        }`}
      >
        Common
      </button>
      {availableClients.map(client => (
        <button
          key={client.key}
          onClick={() => setRuleScope(client.key)}
          className={`px-3 py-1 rounded-full border transition-all cursor-pointer ${
            ruleScope === client.key
              ? "bg-[#EEF0FF] border-[#4F46E5] text-[#4F46E5]"
              : "bg-[#FFFFFF] border-[#E7E4DD] text-[#6B6B76] hover:text-[#17171A]"
          }`}
        >
          {client.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#FFFFFF] min-h-0 flex-1" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Title Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7E4DD] flex-shrink-0">
        <span className="text-[17px] font-semibold text-[#17171A]">Copyedit Review</span>
        <div className="flex items-center gap-2">
          <button
            onClick={runScan}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#4F46E5] hover:bg-[#4338CA] transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
          >
            <PlayCircleOutlined />
            <span>{scanning ? "Scanning..." : "Scan"}</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-[#F4F3EF] rounded-md transition-colors text-[#6B6B76] hover:text-[#17171A] flex items-center justify-center cursor-pointer"
              title="Close Panel"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content Body */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 right-panel-scroll flex flex-col">
        {/* Tabs */}
        <div className="flex bg-[#F4F3EF] p-1 rounded-lg mb-4 flex-shrink-0">
          <button
            onClick={() => setActiveTab("suggestions")}
            className={`flex-1 py-1.5 text-xs font-semibold text-center rounded-md transition-all cursor-pointer ${
              activeTab === "suggestions"
                ? "bg-[#FFFFFF] text-[#17171A] shadow-sm"
                : "text-[#6B6B76] hover:text-[#17171A]"
            }`}
          >
            Proposed Edits {toReviewCount > 0 && `(${toReviewCount})`}
          </button>
          <button
            onClick={() => setActiveTab("rules")}
            className={`flex-1 py-1.5 text-xs font-semibold text-center rounded-md transition-all cursor-pointer ${
              activeTab === "rules"
                ? "bg-[#FFFFFF] text-[#17171A] shadow-sm"
                : "text-[#6B6B76] hover:text-[#17171A]"
            }`}
          >
            Style Rules
          </button>
        </div>

        {activeTab === "suggestions" ? (
          <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
            {/* Progress Section */}
            <div className="bg-[#FAF9F6] border border-[#E7E4DD] rounded-xl p-3.5 flex flex-col gap-3">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-[#6B6B76]">Review Progress</span>
                <span className="text-[#17171A]">{progressPercent}%</span>
              </div>
              <div className="w-full bg-[#E7E4DD] h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-[#4F46E5] h-full rounded-full transition-all duration-350"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>

              {/* Quick Actions (Global Approve/Reject All) */}
              {toReviewCount > 0 && (
                <div className="flex gap-2 border-t border-[#E7E4DD] pt-3 mt-1">
                  <button
                    onClick={handleApproveAll}
                    className="flex-1 py-1.5 rounded-lg border border-[#D5F5E3] bg-[#E8F8F5] text-[#196F3D] text-[11px] font-semibold hover:bg-[#D5F5E3] transition-colors cursor-pointer"
                  >
                    ✓ Accept All
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 py-1.5 rounded-lg border border-[#FADBD8] bg-[#FDEDEC] text-[#943126] text-[11px] font-semibold hover:bg-[#FADBD8] transition-colors cursor-pointer"
                  >
                    ✕ Dismiss All
                  </button>
                </div>
              )}
            </div>

            {/* Status Sub-tabs */}
            <div className="flex gap-1.5 border-b border-[#E7E4DD] pb-2 text-xs">
              <button
                onClick={() => setFilterStatus("to_review")}
                className={`pb-1.5 font-semibold px-1 border-b-2 transition-all cursor-pointer ${
                  filterStatus === "to_review"
                    ? "border-[#4F46E5] text-[#4F46E5]"
                    : "border-transparent text-[#6B6B76] hover:text-[#17171A]"
                }`}
              >
                To Review ({toReviewCount})
              </button>
              <button
                onClick={() => setFilterStatus("accepted")}
                className={`pb-1.5 font-semibold px-1 border-b-2 transition-all cursor-pointer ${
                  filterStatus === "accepted"
                    ? "border-[#4F46E5] text-[#4F46E5]"
                    : "border-transparent text-[#6B6B76] hover:text-[#17171A]"
                }`}
              >
                Accepted ({acceptedCount})
              </button>
              <button
                onClick={() => setFilterStatus("dismissed")}
                className={`pb-1.5 font-semibold px-1 border-b-2 transition-all cursor-pointer ${
                  filterStatus === "dismissed"
                    ? "border-[#4F46E5] text-[#4F46E5]"
                    : "border-transparent text-[#6B6B76] hover:text-[#17171A]"
                }`}
              >
                Dismissed ({dismissedCount})
              </button>
            </div>

            {/* Scope Filters & Collapse All option */}
            {filterStatus === "to_review" && (
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {renderScopeSelector()}
                {suggestions.length > 0 && (
                  <button
                    onClick={toggleAllCollapse}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-[#4F46E5] bg-[#EEF0FF] hover:bg-[#E0E7FF] border border-[#C7D2FE] rounded-lg transition-colors cursor-pointer ml-auto"
                  >
                    {areAllCollapsed ? (
                      <>
                        <DownOutlined className="text-[10px]" />
                        <span>Expand All</span>
                      </>
                    ) : (
                      <>
                        <RightOutlined className="text-[10px]" />
                        <span>Collapse All</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Suggestions List grouped by Style / Rule Name */}
            <div className="flex flex-col gap-3.5 flex-1">
              {filterStatus === "to_review" && suggestions.length > 0 ? (
                suggestions.map(group => {
                  const isCollapsed = !!collapsedRules[group.ruleId];
                  return (
                    <div
                      key={group.ruleId}
                      className="bg-white border border-[#E7E4DD] rounded-xl overflow-hidden shadow-xs transition-shadow hover:shadow-sm"
                    >
                      {/* Style Rule Header (Collapsible & with "Apply All" for this style) */}
                      <div
                        onClick={() => toggleCollapseRule(group.ruleId)}
                        className="flex items-center justify-between p-3 bg-[#FAF9F6] hover:bg-[#F4F3EF] cursor-pointer transition-colors border-b border-[#E7E4DD]"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                          <span className="text-[#6B6B76] text-xs flex items-center">
                            {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                          </span>
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className="text-[13px] font-bold text-[#17171A] truncate">
                              {group.ruleName}
                            </span>
                            <span className="text-[10px] font-semibold text-[#4F46E5] bg-[#EEF0FF] border border-[#C7D2FE] px-1.5 py-0.5 rounded">
                              {group.category}
                            </span>
                            <span className="text-[10px] font-medium text-[#6B6B76] bg-[#E7E4DD]/60 px-1.5 py-0.5 rounded">
                              {group.origin}
                            </span>
                          </div>
                        </div>

                        {/* Actions for this single style rule */}
                        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleApproveStyle(group.ruleId)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#E8F8F5] border border-[#A2E9D8] text-[#0E6251] hover:bg-[#D1F2EB] text-[11px] font-semibold transition-colors cursor-pointer"
                            title={`Apply all ${group.items.length} edits under ${group.ruleName}`}
                          >
                            <CheckOutlined className="text-[10px]" />
                            <span>Apply All ({group.items.length})</span>
                          </button>
                          <button
                            onClick={() => handleRejectStyle(group.ruleId)}
                            className="flex items-center justify-center w-7 h-7 rounded-lg border border-[#FADBD8] text-[#943126] hover:bg-[#FDEDEC] transition-colors cursor-pointer"
                            title={`Dismiss all edits under ${group.ruleName}`}
                          >
                            <CloseOutlined className="text-[11px]" />
                          </button>
                        </div>
                      </div>

                      {/* Items under this style rule */}
                      {!isCollapsed && (
                        <div className="p-3 flex flex-col gap-2.5 bg-white">
                          {group.items.map((item, idx) => (
                            <div
                              key={item.uid}
                              onClick={() => scrollToItem(item)}
                              className="p-2.5 bg-[#FAF9F6] border border-[#E7E4DD] hover:border-[#4F46E5] transition-all rounded-lg cursor-pointer"
                            >
                              {/* Diff Block */}
                              <div className="flex flex-col gap-1.5 text-xs mb-2.5 font-mono">
                                <div className="flex items-start gap-1 bg-[#FDEDEC] text-[#943126] border border-[#FADBD8] rounded px-2.5 py-1.5">
                                  <span className="font-bold opacity-60">-</span>
                                  <span className="break-all">{item.text || "(empty)"}</span>
                                </div>
                                <div className="flex items-start gap-1 bg-[#E8F8F5] text-[#196F3D] border border-[#D5F5E3] rounded px-2.5 py-1.5">
                                  <span className="font-bold opacity-60">+</span>
                                  <span className="break-all">{item.suggested || "(removed)"}</span>
                                </div>
                              </div>

                              {/* Footer Row: Char Position + Action Buttons */}
                              <div className="flex items-center justify-between gap-1.5" onClick={e => e.stopPropagation()}>
                                {suggestionPositions[item.uid] !== undefined ? (
                                  <span className="font-mono text-[10px] font-semibold text-[#4F46E5] bg-[#EEF0FF] border border-[#C7D2FE] px-1.5 py-0.5 rounded">
                                    Char {suggestionPositions[item.uid].toLocaleString()}
                                  </span>
                                ) : (
                                  <span />
                                )}
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => handleReject(group.ruleId, idx)}
                                    className="flex items-center justify-center px-2.5 h-6 rounded-md border border-[#E7E4DD] text-[#EF4444] hover:bg-[#FDEDEC] text-[11px] font-medium transition-colors cursor-pointer"
                                  >
                                    Dismiss
                                  </button>
                                  <button
                                    onClick={() => handleApprove(group.ruleId, idx)}
                                    className="flex items-center justify-center px-3 h-6 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] text-white font-semibold text-[11px] transition-colors cursor-pointer"
                                  >
                                    Approve
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : filterStatus === "to_review" ? (
                <div className="text-center text-xs text-[#9A988F] py-8">
                  No active suggestions. Run "Scan" to find potential edits.
                </div>
              ) : null}

              {filterStatus === "accepted" && (
                acceptedSuggestions.length > 0 ? (
                  acceptedSuggestions.map((item, idx) => (
                    <div key={idx} className="bg-white border border-[#E7E4DD] rounded-xl p-3.5 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[12px] font-bold text-[#17171A]">{item.ruleName}</span>
                        <span className="text-[9px] font-bold text-[#196F3D] bg-[#E8F8F5] px-1.5 py-0.5 rounded">
                          ✓ Accepted
                        </span>
                      </div>
                      <div className="text-xs font-mono bg-[#E8F8F5] text-[#196F3D] border border-[#D5F5E3] rounded px-2.5 py-1.5 break-all">
                        {item.suggested}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-xs text-[#9A988F] py-8">No accepted suggestions.</div>
                )
              )}

              {filterStatus === "dismissed" && (
                dismissedSuggestions.length > 0 ? (
                  dismissedSuggestions.map((item, idx) => (
                    <div key={idx} className="bg-white border border-[#E7E4DD] rounded-xl p-3.5 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[12px] font-bold text-[#17171A]">{item.ruleName}</span>
                        <span className="text-[9px] font-bold text-[#943126] bg-[#FDEDEC] px-1.5 py-0.5 rounded">
                          ✕ Dismissed
                        </span>
                      </div>
                      <div className="text-xs font-mono bg-[#FDEDEC] text-[#943126] border border-[#FADBD8] rounded px-2.5 py-1.5 break-all">
                        {item.text}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-xs text-[#9A988F] py-8">No dismissed suggestions.</div>
                )
              )}
            </div>
          </div>
        ) : (
          /* Rules Config tab grouped by Style Categories */
          <div className="flex flex-col gap-3.5 flex-1 overflow-y-auto">
            <div className="bg-[#FAF9F6] border border-[#E7E4DD] rounded-xl p-3 flex flex-col gap-3">
              <Input
                placeholder="Search copyedit rules or categories..."
                prefix={<SearchOutlined className="text-[#9A988F]" />}
                value={ruleSearch}
                onChange={e => setRuleSearch(e.target.value)}
                className="rounded-lg"
                allowClear
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {renderScopeSelector()}
                {categoryNames.length > 0 && (
                  <button
                    onClick={toggleAllCategoriesCollapse}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-[#4F46E5] bg-[#EEF0FF] hover:bg-[#E0E7FF] border border-[#C7D2FE] rounded-lg transition-colors cursor-pointer ml-auto"
                  >
                    {areAllCategoriesCollapsed ? (
                      <>
                        <DownOutlined className="text-[10px]" />
                        <span>Expand All</span>
                      </>
                    ) : (
                      <>
                        <RightOutlined className="text-[10px]" />
                        <span>Collapse All</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {Object.entries(rulesByCategory).map(([category, rules]) => {
                const isCatCollapsed = !!collapsedCategories[category];
                const allCategoryEnabled = rules.every(r => enabledRules[r.id] !== false);
                const someCategoryEnabled = rules.some(r => enabledRules[r.id] !== false);
                const enabledCount = rules.filter(r => enabledRules[r.id] !== false).length;

                return (
                  <div key={category} className="bg-white border border-[#E7E4DD] rounded-xl overflow-hidden shadow-xs transition-shadow hover:shadow-sm">
                    {/* Category Header (Collapsible) */}
                    <div
                      onClick={() => toggleCollapseCategory(category)}
                      className="flex items-center justify-between px-3.5 py-2.5 bg-[#FAF9F6] hover:bg-[#F4F3EF] cursor-pointer transition-colors border-b border-[#E7E4DD]"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[#6B6B76] text-xs flex items-center">
                          {isCatCollapsed ? <RightOutlined /> : <DownOutlined />}
                        </span>
                        <span className="text-xs font-bold text-[#17171A]">{category}</span>
                        <span className="text-[10px] text-[#6B6B76] bg-[#E7E4DD]/70 px-1.5 py-0.5 rounded-full font-medium">
                          {enabledCount}/{rules.length} enabled
                        </span>
                      </div>
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <span className="text-[11px] text-[#6B6B76]">All</span>
                        <Switch
                          checked={allCategoryEnabled}
                          indeterminate={someCategoryEnabled && !allCategoryEnabled}
                          onChange={(checked) => toggleCategory(category, checked)}
                          size="small"
                        />
                      </div>
                    </div>

                    {/* Rules within this category (hidden when collapsed) */}
                    {!isCatCollapsed && (
                      <div className="divide-y divide-[#E7E4DD]/60">
                        {rules.map(rule => (
                          <div
                            key={rule.id}
                            className="flex items-start justify-between p-3 hover:bg-[#FAF9F6]/50 transition-colors"
                          >
                            <div className="flex flex-col gap-1 pr-3 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-[#17171A]">{rule.name}</span>
                                <span className="text-[9px] font-semibold text-[#4F46E5] bg-[#EEF0FF] border border-[#C7D2FE] px-1.5 py-0.5 rounded">
                                  {rule.origin}
                                </span>
                              </div>
                              <span className="text-[11px] text-[#6B6B76]">{rule.description}</span>
                            </div>
                            <Switch
                              checked={enabledRules[rule.id] !== false}
                              onChange={(checked) => toggleRule(rule.id, checked)}
                              size="small"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {Object.keys(rulesByCategory).length === 0 && (
                <div className="text-center text-xs text-[#9A988F] py-8">
                  No rules found matching your filter.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ClientCopyeditPanel;
